import { Check, ChevronDown, Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '../ui/elements';

const LANGUAGES = [
  { code: 'en', label: '🇬🇧 English' },
  { code: 'fr', label: '🇫🇷 Français' },
  // { code: 'ar', label: '🇸🇦 العربية' },
] as const;

type LanguageCode = (typeof LANGUAGES)[number]['code'];

function LanguageSelector() {
  const { i18n } = useTranslation('login');
  const [open, setOpen] = useState(false);

  // Normalize language like "en-US" -> "en"
  const currentLang = useMemo<LanguageCode>(() => {
    const raw = i18n.resolvedLanguage || i18n.language || 'en';
    const base = raw.split('-')[0] as LanguageCode;
    return (
      LANGUAGES.some((l) => l.code === base) ? base : 'en'
    ) as LanguageCode;
  }, [i18n.language, i18n.resolvedLanguage]);

  const currentLabel = useMemo(() => {
    return LANGUAGES.find((l) => l.code === currentLang)?.label ?? '🇬🇧 English';
  }, [currentLang]);

  useEffect(() => {
    document.documentElement.lang = currentLang;
  }, [currentLang]);

  const handleLanguageChange = useCallback(
    (lang: LanguageCode) => {
      i18n.changeLanguage(lang); // LanguageDetector auto-saves to localStorage
      setOpen(false);
    },
    [i18n]
  );

  return (
    <div className="absolute right-8 top-8">
      <div className="relative">
        {/*  */}

        <Button
          variant="languageSelectTrigger"
          onClick={() => setOpen((prev) => !prev)}
        >
          <Globe
            className="w-4 h-4 text-slate-500"
            aria-hidden="true"
            data-testid="globe-icon"
          />
          <span data-slot="select-value" className="pointer-events-none">
            {' '}
            {currentLabel}{' '}
          </span>
          <ChevronDown className="size-4 opacity-50" aria-hidden="true" />
        </Button>

        {/*  */}
        {open && (
          <div className="mt-1 z-40 bg-white transition-all duration-300 ease-in-out">
            <div className="z-50 mt-2 max-h-80 overflow-x-hidden overflow-y-auto rounded-xl border border-black/10 bg-white p-1 shadow-md">
              <div className="flex flex-col">
                {LANGUAGES.map(({ code, label }) => {
                  const isSelected = code === currentLang;

                  return (
                    <button
                      key={code}
                      type="button"
                      className={[
                        'text-sm justify-between hover:bg-gray-200 hover:rounded-xl cursor-pointer px-3 py-1.5 text-left',
                        'flex items-center gap-2',
                      ].join(' ')}
                      onClick={() => handleLanguageChange(code)}
                    >
                      <span>{label}</span>

                      {isSelected ? (
                        <span className="w-4 flex items-center justify-center">
                          <Check className="size-4" aria-hidden="true" />
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default LanguageSelector;
